import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import NavBar from "../common/NavBar";
import Footer from "../common/Footer";
import Pagination from "../common/Pagination";

const PAGE_SIZE = 5;

const parseProscons = (comment) => {
  if (!comment) return { pros: "", cons: "" };
  const prosMatch = comment.match(/Pros:\s*([\s\S]*?)(?=\nCons:|$)/);
  const consMatch = comment.match(/Cons:\s*([\s\S]*)$/);
  return {
    pros: prosMatch ? prosMatch[1].trim() : "",
    cons: consMatch ? consMatch[1].trim() : "",
  };
};

const getStars = (value) => Math.min(5, Math.max(1, Math.ceil(value)));

const StarRating = ({ stars }) => (
  <div className="flex text-xl">
    {[1, 2, 3, 4, 5].map((i) => (
      <span key={i} className={i <= stars ? "text-yellow-400" : "text-gray-300"}>
        ★
      </span>
    ))}
  </div>
);

const ApartmentReviews = () => {
  const { t } = useTranslation();
  const { id } = useParams();
  const [apartment, setApartment] = useState(null);
  const [reviews, setReviews] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);

  const getApiBaseUrl = () =>
    process.env.REACT_APP_API_URL || "http://localhost:8000";

  useEffect(() => {
    fetch(`${getApiBaseUrl()}/api/properties/apartments/${id}/`)
      .then((res) => res.json())
      .then((data) => setApartment(data))
      .catch((err) => console.error("Error fetching apartment:", err));

    fetch(`${getApiBaseUrl()}/api/reviews/?apartment_id=${id}`)
      .then((res) => res.json())
      .then((data) => {
        const sorted = [...data].sort((a, b) => {
          const da = a.start_date || (a.created_at || "").slice(0, 10);
          const db = b.start_date || (b.created_at || "").slice(0, 10);
          return db.localeCompare(da);
        });
        setReviews(sorted);
      })
      .catch((err) => console.error("Error fetching reviews:", err));
  }, [id]);

  // Inject JSON-LD schema when reviews and apartment are both loaded
  useEffect(() => {
    if (!reviews.length || !apartment) return;

    const reviewCount = reviews.length;
    const avgRating = (
      reviews.reduce((sum, r) => sum + r.value, 0) / reviewCount
    ).toFixed(1);

    const schema = {
      "@context": "https://schema.org",
      "@type": "Accommodation",
      name: apartment.apartment_name,
      aggregateRating: {
        "@type": "AggregateRating",
        ratingValue: avgRating,
        reviewCount: String(reviewCount),
      },
      review: reviews.map((r) => ({
        "@type": "Review",
        datePublished: r.created_at
          ? r.created_at.slice(0, 10)
          : r.start_date || "",
        reviewBody: r.comment || "",
        reviewRating: {
          "@type": "Rating",
          bestRating: "10",
          ratingValue: String(r.value),
          worstRating: "0",
        },
      })),
    };

    const script = document.createElement("script");
    script.type = "application/ld+json";
    script.id = "apartment-reviews-schema";
    script.text = JSON.stringify(schema);
    document.head.appendChild(script);

    return () => {
      const existing = document.getElementById("apartment-reviews-schema");
      if (existing) existing.remove();
    };
  }, [reviews, apartment]);

  const paged = reviews.slice(
    (currentPage - 1) * PAGE_SIZE,
    currentPage * PAGE_SIZE
  );

  return (
    <div className="min-h-screen flex flex-col">
      <NavBar />
      <div className="flex flex-col items-center mt-20 px-4 pb-10 flex-1">
        <h1 className="text-2xl md:text-3xl font-bold mb-1 text-center">
          {apartment ? apartment.apartment_name : t("loading")}
        </h1>
        <h2 className="text-lg text-gray-600 mb-4">{t("reviews")}</h2>
        <Link
          to={`/apartments/${id}`}
          className="text-blue-600 hover:underline mb-6 text-sm"
        >
          ← {t("back_to_apartment")}
        </Link>

        {reviews.length === 0 ? (
          <p className="text-gray-500 mt-4">{t("no_reviews_found")}</p>
        ) : (
          <>
            {/* Desktop table */}
            <div className="hidden md:block w-full max-w-5xl overflow-x-auto">
              <table className="min-w-full bg-white border border-gray-300 rounded-lg">
                <thead>
                  <tr className="bg-gray-100 text-center">
                    <th className="py-3 px-4 border-b whitespace-nowrap">
                      {t("start_date")}
                    </th>
                    <th className="py-3 px-4 border-b">{t("pros")}</th>
                    <th className="py-3 px-4 border-b">{t("cons")}</th>
                    <th className="py-3 px-4 border-b whitespace-nowrap">
                      {t("rating")}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {paged.map((review) => {
                    const { pros, cons } = parseProscons(review.comment);
                    return (
                      <tr
                        key={review.id}
                        className="hover:bg-gray-50 align-top"
                      >
                        <td className="py-3 px-4 border-b whitespace-nowrap text-center text-sm text-gray-600">
                          {review.start_date ||
                            (review.created_at || "").slice(0, 10)}
                        </td>
                        <td className="py-3 px-4 border-b text-left text-green-700 text-sm">
                          {pros || "-"}
                        </td>
                        <td className="py-3 px-4 border-b text-left text-red-600 text-sm">
                          {cons || "-"}
                        </td>
                        <td className="py-3 px-4 border-b">
                          <div className="flex justify-center">
                            <StarRating stars={getStars(review.value)} />
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Mobile cards */}
            <div className="md:hidden w-full flex flex-col gap-4">
              {paged.map((review) => {
                const { pros, cons } = parseProscons(review.comment);
                return (
                  <div
                    key={review.id}
                    className="bg-white rounded-lg shadow p-4 border border-gray-200"
                  >
                    <div className="flex justify-between items-center mb-2">
                      <span className="text-sm text-gray-500">
                        {review.start_date ||
                          (review.created_at || "").slice(0, 10)}
                      </span>
                      <StarRating stars={getStars(review.value)} />
                    </div>
                    {pros && (
                      <p className="text-green-700 text-sm mt-1">
                        <span className="font-semibold">{t("pros")}:</span>{" "}
                        {pros}
                      </p>
                    )}
                    {cons && (
                      <p className="text-red-600 text-sm mt-1">
                        <span className="font-semibold">{t("cons")}:</span>{" "}
                        {cons}
                      </p>
                    )}
                  </div>
                );
              })}
            </div>

            <div className="mt-4">
              <Pagination
                totalItems={reviews.length}
                pageSize={PAGE_SIZE}
                currentPage={currentPage}
                onPageChange={setCurrentPage}
              />
            </div>
          </>
        )}
      </div>
      <Footer />
    </div>
  );
};

export default ApartmentReviews;
